import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

/**
 * 支持搜索的下拉选择组件
 * @param {string} value - 当前选中的值
 * @param {Array} options - 选项列表 [{value, label, searchText?, disabled?}]，searchText 为额外搜索文本
 * @param {function} onChange - 值变化回调
 * @param {string} placeholder - 占位文本
 * @param {boolean} searchable - 是否支持搜索，默认 true
 * @param {boolean} multiple - 是否支持多选，默认 false
 * @param {boolean} clearable - 是否显示清空按钮，默认 false
 * @param {boolean} disabled - 是否禁用
 * @param {string} className - 自定义类名
 * @param {object} style - 自定义样式
 */
const SearchableSelect = ({
  value,
  options = [],
  onChange,
  placeholder = '请选择',
  searchable = true,
  multiple = false,
  clearable = false,
  clearLabel = '清空选项',
  disabled = false,
  className = '',
  style = {},
  emptyText = '无匹配选项'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownDirection, setDropdownDirection] = useState('down');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // 过滤后的选项
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter(opt =>
      opt.label.toLowerCase().includes(lowerSearch) ||
      (opt.searchText || '').toLowerCase().includes(lowerSearch)
    );
  }, [options, search]);

  // 选中标签
  const selectedOption = useMemo(() => {
    if (multiple) return null;
    return options.find(opt => opt.value === value);
  }, [options, value, multiple]);

  const selectedValues = useMemo(() => {
    if (!multiple) return [];
    return Array.isArray(value) ? value : [];
  }, [value, multiple]);

  const selectedLabel = useMemo(() => {
    if (!multiple) {
      return selectedOption ? selectedOption.label : placeholder;
    }
    if (selectedValues.length === 0) {
      return placeholder;
    }
    const labels = selectedValues.map(selectedValue => {
      const option = options.find(opt => opt.value === selectedValue);
      return option ? option.label : selectedValue;
    });
    return labels.join(', ');
  }, [multiple, selectedOption, selectedValues, options, placeholder]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedTrigger = containerRef.current && containerRef.current.contains(e.target);
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedTrigger && !clickedDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 打开时聚焦搜索框
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isOpen, searchable]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const updateDropdownDirection = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const expectedDropdownHeight = searchable ? 292 : 244;
      const nextDirection = spaceBelow < expectedDropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down';
      setDropdownDirection(nextDirection);
      setDropdownStyle({
        position: 'fixed',
        left: `${rect.left}px`,
        right: 'auto',
        width: `${rect.width}px`,
        top: nextDirection === 'down' ? `${rect.bottom + 4}px` : 'auto',
        bottom: nextDirection === 'up' ? `${window.innerHeight - rect.top + 4}px` : 'auto'
      });
    };

    updateDropdownDirection();
    window.addEventListener('resize', updateDropdownDirection);
    window.addEventListener('scroll', updateDropdownDirection, true);
    return () => {
      window.removeEventListener('resize', updateDropdownDirection);
      window.removeEventListener('scroll', updateDropdownDirection, true);
    };
  }, [isOpen, searchable]);

  const handleSelect = (option) => {
    if (option.disabled) return;
    if (multiple) {
      const nextValues = selectedValues.includes(option.value)
        ? selectedValues.filter(item => item !== option.value)
        : [...selectedValues, option.value];
      onChange(nextValues);
      setSearch('');
      return;
    }
    onChange(option.value);
    setIsOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Enter' && filteredOptions.length === 1) {
      handleSelect(filteredOptions[0]);
    }
  };

  const isSelected = (option) => multiple
    ? selectedValues.includes(option.value)
    : option.value === value;
  const showPlaceholder = multiple ? selectedValues.length === 0 : !selectedOption;
  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      className={`searchable-select-dropdown searchable-select-dropdown-${dropdownDirection}`}
      style={dropdownStyle}
    >
      {searchable && (
        <div className="searchable-select-search">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="搜索..."
            className="searchable-select-search-input"
          />
        </div>
      )}

      <div className="searchable-select-options">
        {filteredOptions.length === 0 ? (
          <div className="searchable-select-empty">{emptyText}</div>
        ) : (
          filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`searchable-select-option ${
                isSelected(option) ? 'searchable-select-option-selected' : ''
              } ${option.disabled ? 'searchable-select-option-disabled' : ''}`}
              onClick={() => handleSelect(option)}
            >
              {option.label}
              {isSelected(option) && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={`searchable-select ${className} ${disabled ? 'searchable-select-disabled' : ''}`}
      style={style}
    >
      <div
        className={`searchable-select-trigger ${isOpen ? 'searchable-select-open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`searchable-select-value ${showPlaceholder ? 'searchable-select-placeholder' : ''}`}>
          {selectedLabel}
        </span>
        {clearable && (multiple ? selectedValues.length > 0 : value !== '' && value != null) && (
          <button
            type="button"
            className="searchable-select-clear"
            aria-label={clearLabel}
            title={clearLabel}
            disabled={disabled}
            onClick={event => {
              event.stopPropagation();
              if (disabled) return;
              onChange(multiple ? [] : '');
              setSearch('');
              setIsOpen(false);
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
        <svg
          className={`searchable-select-arrow ${isOpen ? 'searchable-select-arrow-up' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </div>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default SearchableSelect;
